import {
  Button,
  Card,
  color,
  Icon,
  Input,
  Modal,
  textStyleBody,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { coreApi } from '@/api';
import { useModalState, useRequest } from '@/hooks';
import { ListStyleTypeDisc, MarginBottom16, MarginBottom24 } from '@/styles';

function DisableChannel({ name }: Pick<RealWorldApp, 'name'>) {
  const { handleSubmit, formState, reset, control } = useForm<Pick<RealWorldApp, 'name'>>({
    mode: 'onChange'
  });
  const history = useHistory();
  const { appId } = useParams<AppParam>();

  const { mutate } = useRequest<RealWorldApp[]>('/apps', undefined, {
    headers: {
      'x-rwd-api-version': '1.1'
    }
  });

  const [isDeleting, setIsDeleting] = useState(false);

  const { modal, openModal, closeModal } = useModalState();

  const onSubmit = handleSubmit(() => {
    openModal({
      title: '정말 채널을 비활성화하시겠어요?',
      cssStyle: textStyleBody,
      children: (
        <>
          <p css={MarginBottom16}>
            채널 구성원이 모두 제거되고, 생성했던 모든 게임이 삭제되어요!
            <br />
            비활성화 이후에는 채널을 복구할 수 없어요!
          </p>
          <p>본 내용에 동의하신다면 비활성화를 진행해주세요</p>
        </>
      ),
      primaryAction: {
        type: 'destructive',
        content: '비활성화',
        icon: 'trash_alt_solid',
        onAction: async () => {
          setIsDeleting(true);

          await coreApi.delete(`/apps/${appId}/disable`, {
            headers: {
              'x-rwd-api-version': '1.1'
            }
          });

          const defaultChannelID = localStorage.getItem('defaultChannelID');

          if (appId === defaultChannelID) {
            localStorage.removeItem('defaultChannelID');
          }

          const apps = await mutate((apps) => apps?.filter((app) => app.id !== appId));

          setIsDeleting(false);

          if (apps?.length) {
            history.push(`/apps/${apps[0].id}`);
          } else {
            history.push('/createApp');
          }
        }
      },
      secondaryAction: {
        content: '취소',
        onAction: () => {
          reset({ name: '' });
          closeModal();
        }
      }
    });
  });

  return (
    <Card
      header={{
        content: '채널 비활성화',
        prefix: <Icon icon="times_regular" color={color.icon_critical} />,
        textColor: color.text_critical,
        backgroundColor: color.surface_critical_subdued_hovered
      }}
    >
      <Modal {...modal} primaryAction={{ ...modal.primaryAction, loading: isDeleting }} />
      <form css={textStyleBody} onSubmit={onSubmit}>
        <p css={MarginBottom16}>
          <b>&apos;{name}&apos;</b> 채널의{' '}
          <Typography as="span" type="body" textColor="critical">
            모든 내용이 즉시 삭제되며, 비활성화 이후에는 복구할 수 없어요!
          </Typography>
        </p>
        <b>유의사항을 확인해주세요!</b>
        <ul css={[ListStyleTypeDisc, MarginBottom16]}>
          <li>소유자, 편집자, 테스터로 지정된 채널 구성원이 모두 제거되어요!</li>
          <li>공개한 게임을 포함하여 생성했던 모든 게임이 일괄 삭제되어요!</li>
        </ul>
        <p css={MarginBottom24}>정말 채널을 비활성화하려면 채널의 이름을 입력해주세요.</p>
        <Controller
          control={control}
          name="name"
          rules={{ required: true, validate: (typing) => typing === name }}
          render={({ field }) => <Input cssStyle={MarginBottom24} {...field} placeholder={name} />}
        />
        <Button
          type="destructive"
          disabled={!formState.isValid}
          icon="trash_alt_solid"
          htmlType="submit"
        >
          비활성화
        </Button>
      </form>
    </Card>
  );
}

export default DisableChannel;
